import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { radius } from "src/design/tokens";
import { fontStretch } from "src/design/typography";
import { ColorModeToggle, StatusChip } from "src/grading/components";

export type StationSyncStatus = "synced" | "pending" | "offline";

export interface StationHeaderProps {
  /** Sync state of the browser outbox; hidden until the scorer has joined. */
  syncStatus: StationSyncStatus | null;
  pending: number;
  /** Clears the scorer token; the button is hidden when nothing has been joined. */
  onLeave?: () => void;
}

/**
 * Slim header for the volunteer station page, which renders outside the console shell (design
 * `isStation` header): flag mark, "Scoring station" with the test name under it, the sync
 * state, the gym-mode toggle and Leave.
 */
export const StationHeader = ({
  syncStatus,
  pending,
  onLeave,
}: StationHeaderProps) => {
  const { t } = useTranslation();
  return (
    <Box
      component="header"
      sx={(theme) => ({
        bgcolor: theme.palette.tokens.surface,
        borderBottom: `1px solid ${theme.palette.divider}`,
      })}
    >
      <Box
        sx={{
          maxWidth: 1100,
          mx: "auto",
          px: { xs: 2, sm: 3 },
          py: 1.5,
          minHeight: 64,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.75,
        }}
      >
        <Box
          aria-hidden
          sx={(theme) => ({
            position: "relative",
            flex: "none",
            width: 34,
            height: 24,
            borderRadius: `${radius.mark}px`,
            overflow: "hidden",
            background: `repeating-linear-gradient(180deg, ${theme.palette.tokens.danger} 0 3.43px, ${theme.palette.tokens.surface} 3.43px 6.86px)`,
            boxShadow: `inset 0 0 0 1px ${theme.palette.tokens.border}`,
          })}
        >
          <Box
            sx={(theme) => ({
              position: "absolute",
              top: 0,
              left: 0,
              width: 14,
              height: 13,
              bgcolor: theme.palette.tokens.primary,
              color: theme.palette.onTier,
              fontSize: 8,
              lineHeight: "13px",
              textAlign: "center",
            })}
          >
            ★
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.375 }}>
          <Typography
            component="p"
            sx={{
              m: 0,
              fontSize: 14,
              fontWeight: 900,
              lineHeight: 1,
              fontStretch: fontStretch.display,
              textTransform: "uppercase",
            }}
          >
            {t("station.header.eyebrow", { defaultValue: "Scoring station" })}
          </Typography>
          <Typography
            component="p"
            sx={(theme) => ({
              m: 0,
              fontSize: 10,
              fontWeight: 600,
              lineHeight: 1,
              letterSpacing: ".14em",
              textTransform: "uppercase",
              color: theme.palette.tokens.textMuted,
            })}
          >
            {t("app.testName")}
          </Typography>
        </Box>
        <Box
          sx={{
            ml: "auto",
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.25,
          }}
        >
          {syncStatus === "synced" ? (
            <Box
              role="status"
              aria-live="polite"
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                fontSize: 12.5,
                fontWeight: 700,
                color: theme.palette.successInk,
              })}
            >
              <Box
                component="span"
                aria-hidden
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "currentColor",
                }}
              />
              {t("station.sync.synced")}
            </Box>
          ) : syncStatus ? (
            <Box role="status" aria-live="polite">
              <StatusChip
                status={syncStatus}
                count={syncStatus === "pending" ? pending : undefined}
              />
            </Box>
          ) : null}
          <ColorModeToggle variant="switch" />
          {onLeave ? (
            <Button
              variant="outlined"
              onClick={onLeave}
              sx={{ minHeight: 48, fontSize: 12, fontWeight: 700 }}
            >
              {t("station.leave", { defaultValue: "Leave" })}
            </Button>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default StationHeader;
